import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { toast } from "sonner";

const Row = ({ label, ok, value, testid }) => (
  <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span
      className={`text-sm font-mono ${ok ? "text-green-500" : "text-destructive"}`}
      data-testid={testid}
    >
      {value}
    </span>
  </div>
);

const AdminDiagnostics = () => {
  const [status, setStatus] = useState(null);
  const [selfTest, setSelfTest] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/admin/integration-status`);
        if (res.ok) setStatus(await res.json());
      } catch {
        /* diagnostics only */
      }
    })();
  }, []);

  const runSelfTest = async () => {
    setRunning(true);
    try {
      const res = await authFetch(`${API}/admin/secrets/self-test`);
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setSelfTest(data);
      if (data.integrations) setStatus(data.integrations);
      const ok = data.scrub_self_test.passed && data.log_scan.verdict !== "fail";
      toast[ok ? "success" : "error"](
        ok
          ? "Key scrubbing verified on this server"
          : "Check FAILED — see the result below"
      );
    } catch {
      toast.error("Self-test failed to run");
    } finally {
      setRunning(false);
    }
  };

  const stripe = status?.stripe;

  return (
    <Card className="bg-card border-border" data-testid="admin-diagnostics">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          Diagnostics — keys &amp; integrations
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Status only. No key value is ever sent to a browser.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          {status ? (
            <>
              <Row
                label="Stripe key"
                ok={stripe.configured}
                value={stripe.configured ? "configured" : "NOT configured"}
                testid="diag-stripe-configured"
              />
              <Row
                label="Stripe mode"
                ok={stripe.mode === "live"}
                value={stripe.mode}
                testid="diag-stripe-mode"
              />
              <Row
                label="Stripe price"
                ok={stripe.price_configured}
                value={stripe.price_configured ? "configured" : "missing"}
                testid="diag-stripe-price"
              />
              <Row
                label="Stripe webhook secret"
                ok={stripe.webhook_secret_configured}
                value={stripe.webhook_secret_configured ? "configured" : "missing"}
                testid="diag-stripe-webhook"
              />
              <Row
                label="Resend"
                ok={status.resend.configured}
                value={status.resend.configured ? "configured" : "missing"}
                testid="diag-resend"
              />
              <Row
                label="LLM key"
                ok={status.llm.configured}
                value={status.llm.configured ? "configured" : "missing"}
                testid="diag-llm"
              />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Loading status…</p>
          )}
        </div>

        <div className="pt-4 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
            <Button
              onClick={runSelfTest}
              disabled={running}
              variant="outline"
              data-testid="run-scrub-self-test"
            >
              {running ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4 mr-2" />
              )}
              Run key-scrubbing self-test
            </Button>
            <p className="text-xs text-muted-foreground">
              Logs a fabricated key-shaped string on this server and reads back what the
              log pipeline emitted. Never uses a real key.
            </p>
          </div>

          {selfTest && (
            <div
              className={`p-4 border ${
                selfTest.scrub_self_test.passed && selfTest.log_scan.verdict !== "fail"
                  ? "border-green-500/40 bg-green-500/5"
                  : "border-destructive/50 bg-destructive/5"
              }`}
              data-testid="scrub-self-test-result"
            >
              <p
                className={`font-semibold flex items-center gap-2 mb-2 ${
                  selfTest.scrub_self_test.passed ? "text-green-500" : "text-destructive"
                }`}
                data-testid="scrub-self-test-verdict"
              >
                {selfTest.scrub_self_test.passed ? (
                  <ShieldCheck className="w-4 h-4" />
                ) : (
                  <ShieldAlert className="w-4 h-4" />
                )}
                {selfTest.scrub_self_test.passed ? "PASS" : "FAIL"} — scrubbing:{" "}
                {selfTest.scrub_self_test.lines_redacted} of {selfTest.scrub_self_test.probes_run}{" "}
                probes redacted, {selfTest.scrub_self_test.leaked_shapes.length} leaked
              </p>
              <p
                className={`font-semibold flex items-center gap-2 mb-2 ${
                  selfTest.log_scan.verdict === "pass"
                    ? "text-green-500"
                    : selfTest.log_scan.verdict === "inconclusive"
                    ? "text-amber-400"
                    : "text-destructive"
                }`}
                data-testid="log-scan-verdict"
              >
                {selfTest.log_scan.verdict === "fail" ? (
                  <ShieldAlert className="w-4 h-4" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                {selfTest.log_scan.verdict === "pass"
                  ? `PASS — logs: 0 key shapes in ${selfTest.log_scan.total_lines_scanned.toLocaleString()} lines scanned`
                  : selfTest.log_scan.verdict === "inconclusive"
                  ? "INCONCLUSIVE — logs: nothing available to scan on this server"
                  : `FAIL — logs: ${selfTest.log_scan.finding_count} key shapes found`}
              </p>
              <p className="text-xs text-muted-foreground mb-2" data-testid="log-scan-detail">
                {selfTest.log_scan.files_scanned.length} log file(s) ·{" "}
                {selfTest.log_scan.emitted_lines_scanned.toLocaleString()} emitted lines held in
                memory · {selfTest.log_scan.note}
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Scrubber attached to: root logger{" "}
                {selfTest.scrub_self_test.attachment.root_logger ? "✓" : "✗"} ·{" "}
                {Object.entries(selfTest.scrub_self_test.attachment.named_loggers)
                  .map(([name, ok]) => `${name} ${ok ? "✓" : "✗"}`)
                  .join(" · ")}
              </p>
              <div className="font-mono text-xs text-muted-foreground space-y-1">
                {selfTest.scrub_self_test.emitted_lines.map((line, i) => (
                  <div key={i} className="truncate" data-testid={`scrub-line-${i}`}>
                    {line}
                  </div>
                ))}
                {selfTest.log_scan.findings.slice(0, 10).map((f, i) => (
                  <div key={`f${i}`} className="truncate text-destructive">
                    {f.file}:{f.line}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDiagnostics;
